import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasFeature } from '../utils/features';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../i18n';
import AboutHospitalCard from './AboutHospitalCard';

const Tooltip = styled.span`
  position: absolute;
  right: 105%;
  background: rgba(17, 24, 39, 0.92);
  color: white;
  font-size: 0.75rem;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  white-space: nowrap;
`;

const Button = styled.button`
  position: fixed;
  bottom: ${(props) => props.$offsetBottom};
  right: 2rem;
  background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.32);
  transition: all 0.3s ease;
  font-size: 1.5rem;
  z-index: 10002;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 16px rgba(239, 68, 68, 0.45);
    ${Tooltip} {
      opacity: 1;
    }
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 52px;
    height: 52px;
    font-size: 1.25rem;
    right: 1.5rem;
    bottom: ${(props) => props.$mobileOffsetBottom};
  }
`;

const MenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 10000;
`;

const MenuPanel = styled.div`
  position: fixed;
  bottom: ${(props) => props.bottomOffset};
  right: 2rem;
  width: 240px;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.15);
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  z-index: 10001;
  transform-origin: bottom right;
  animation: fadeInUp 0.18s ease;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(12px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 768px) {
    right: 1.25rem;
    width: calc(100% - 2.5rem);
    bottom: ${(props) => props.mobileBottomOffset};
  }
`;

const MenuItem = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0.65rem 0.75rem;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background: #f1f5f9;
    transform: translateY(-1px);
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 0.25rem 0;
`;

const MenuIcon = styled.span`
  font-size: 1.35rem;
  line-height: 1;
`;

const MenuText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const MenuLabel = styled.span`
  font-weight: 600;
  color: #0f172a;
  font-size: 0.95rem;
`;

const MenuDescription = styled.span`
  font-size: 0.8rem;
  color: #475569;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: ${(props) => (props.open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  padding: 2rem;
  z-index: 10001;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ModalContent = styled.div`
  position: relative;
  width: min(960px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 24px;
  background: #f8fafc;
  box-shadow: 0 25px 45px rgba(15, 23, 42, 0.28);
  padding: 2rem 2.5rem;
  padding-top: 3.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
    padding-top: 3rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    padding-top: 2.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  background: rgba(15, 23, 42, 0.08);
  border: none;
  border-radius: 9999px;
  padding: 0.5rem 0.875rem;
  color: #0f172a;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  font-size: 0.875rem;
  white-space: nowrap;

  &:hover {
    background: rgba(15, 23, 42, 0.16);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    padding: 0.4rem 0.75rem;
    font-size: 0.8125rem;
  }

  @media (max-width: 480px) {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.35rem 0.625rem;
    font-size: 0.75rem;
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 1.5rem;
  padding-right: 4rem;

  @media (max-width: 768px) {
    padding-right: 3.5rem;
  }

  @media (max-width: 480px) {
    padding-right: 3rem;
  }
`;

const ModalTitle = styled.h2`
  margin: 0 0 0.35rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
`;

const ModalSubtitle = styled.p`
  margin: 0;
  color: #475569;
  font-size: 0.95rem;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #475569;
  font-weight: 500;
`;

const ErrorState = styled.div`
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  gap: 0.5rem;
  font-weight: 500;
`;

const AboutCardModal = styled(AboutHospitalCard)`
  margin-bottom: 0;
`;

const LanguageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

const LanguageOptionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 12px;
  border: 2px solid ${props => props.$active ? '#0B63C5' : '#e5e7eb'};
  background: ${props => props.$active ? 'rgba(102, 126, 234, 0.08)' : 'white'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    border-color: #0B63C5;
    background: rgba(102, 126, 234, 0.12);
    transform: translateY(-2px);
  }
`;

const LanguageBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(102, 126, 234, 0.15);
  color: #0B63C5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
`;

const LanguageNativeName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
`;

const LanguageLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const FloatingAboutButton = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { i18n, t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  // Check for doctor user in localStorage (doctors use separate storage)
  // Check on every render to detect login changes
  const doctorUser = (() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('doctorUser');
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  })();

  // Determine user role - check both AuthContext and localStorage
  const currentUser = user || doctorUser;
  const isPatient = currentUser?.role === 'patient';
  const isDoctor = currentUser?.role === 'doctor';
  
  // Get token - use doctorToken for doctors, regular token for patients
  const currentToken = isDoctor 
    ? (typeof window !== 'undefined' ? localStorage.getItem('doctorToken') : null) || token
    : token;

  // Settings button offset - above Notification Bell
  // Order from bottom: Notification Bell (2rem) -> Settings (2rem + 80px)
  // Read Aloud button removed temporarily (kept in TTSPlayer.jsx for future use)
  const bottomOffset = isPatient ? 'calc(2rem + 80px)' : '2rem';
  const mobileOffset = isPatient ? 'calc(1.5rem + 70px)' : '1.5rem';
  const menuBottomOffset = isPatient ? 'calc(2rem + 80px + 4.5rem)' : 'calc(2rem + 4.5rem)';
  const menuMobileBottomOffset = isPatient
    ? 'calc(1.5rem + 70px + 4.25rem)'
    : 'calc(1.5rem + 4.25rem)';

  // All hooks must be called before any conditional returns
  const loadHospitalProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/profile/hospital', {
        headers: currentToken
          ? {
              Authorization: `Bearer ${currentToken}`
            }
          : undefined
      });
      setProfile(response.data.profile);
    } catch (fetchError) {
      console.error('Error loading hospital profile:', fetchError);
      const message =
        fetchError.response?.data?.message || 'Failed to load hospital profile';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [currentToken]);


  const closeMenu = () => setMenuOpen(false);
  const closeAbout = () => setAboutOpen(false);
  const closeLanguage = () => setLanguageOpen(false);

  const handleProfileClick = () => {
    closeMenu();
    if (isPatient) {
      navigate('/profile');
    } else if (isDoctor) {
      navigate('/doctor?section=profile');
    }
  };


  const handleAboutClick = async () => {
    closeMenu();
    setAboutOpen(true);
    if (!profile) {
      await loadHospitalProfile();
    } else {
      setError(null);
    }
  };

  const handleLanguageClick = () => {
    closeMenu();
    setLanguageOpen(true);
  };

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    if (typeof window !== 'undefined') {
      // Store language preference per user
      const userId = currentUser?.patientId || currentUser?._id || currentUser?.id;
      if (userId) {
        localStorage.setItem(`preferredLanguage_${userId}`, code);
      } else {
        localStorage.setItem('preferredLanguage', code);
      }
    }
    const selectedLang = LANGUAGES.find(l => l.code === code);
    toast.success(t('profile.languageChanged', { 
      language: selectedLang?.nativeName || code,
      defaultValue: `Language changed to ${selectedLang?.nativeName || code}`
    }));
    closeLanguage();
  };

  const currentLanguage = LANGUAGES.find(l => l.code === (i18n.language?.split('-')[0] || 'en')) || LANGUAGES[0];

  const variant = isDoctor ? 'doctor' : 'patient';

  return (
    <>
      {menuOpen && <MenuBackdrop onClick={closeMenu} />}
      
      <Button
        type="button"
        onClick={() => setMenuOpen((prev) => !prev)}
        $offsetBottom={bottomOffset}
        $mobileOffsetBottom={mobileOffset}
        title="Open settings"
        aria-label="Open settings menu"
      >
        ⚙️
        <Tooltip>Settings</Tooltip>
      </Button>

      {menuOpen && (
        <MenuPanel
          bottomOffset={menuBottomOffset}
          mobileBottomOffset={menuMobileBottomOffset}
        >
          <MenuItem type="button" onClick={handleProfileClick}>
            <MenuIcon>👤</MenuIcon>
            <MenuText>
              <MenuLabel>Account Settings</MenuLabel>
              <MenuDescription>Manage your profile details and contact info</MenuDescription>
            </MenuText>
          </MenuItem>

          {hasFeature(user, 'translation') && (
            <MenuItem type="button" onClick={handleLanguageClick}>
              <MenuIcon>🌐</MenuIcon>
              <MenuText>
                <MenuLabel>Language</MenuLabel>
                <MenuDescription>Change your preferred language ({currentLanguage.nativeName})</MenuDescription>
              </MenuText>
            </MenuItem>
          )}

          <MenuDivider />

          <MenuItem type="button" onClick={handleAboutClick}>
            <MenuIcon>ℹ️</MenuIcon>
            <MenuText>
              <MenuLabel>About Us</MenuLabel>
              <MenuDescription>Learn more about the hospital and care team</MenuDescription>
            </MenuText>
          </MenuItem>
        </MenuPanel>
      )}

      <ModalOverlay open={aboutOpen} onClick={closeAbout}>
        <ModalContent onClick={(event) => event.stopPropagation()}>
          <CloseButton type="button" onClick={closeAbout}>
            Close ✕
          </CloseButton>
          <ModalHeader>
            <ModalTitle>About Our Hospital</ModalTitle>
            <ModalSubtitle>
              Learn more about the facility, leadership, and services available.
            </ModalSubtitle>
          </ModalHeader>

          {loading && !profile ? (
            <LoadingState>Loading About Us information...</LoadingState>
          ) : error ? (
            <ErrorState>
              <span>{error}</span>
              <button
                type="button"
                onClick={loadHospitalProfile}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '9999px',
                  border: 'none',
                  background: '#2563eb',
                  color: 'white',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </ErrorState>
          ) : profile ? (
            <AboutCardModal profile={profile} variant={variant} />
          ) : (
            <LoadingState>Loading About Us information...</LoadingState>
          )}
        </ModalContent>
      </ModalOverlay>

      {/* Language Selection Modal */}
      <ModalOverlay open={languageOpen} onClick={closeLanguage}>
        <ModalContent onClick={(event) => event.stopPropagation()}>
          <CloseButton type="button" onClick={closeLanguage}>
            Close ✕
          </CloseButton>
          <ModalHeader>
            <ModalTitle>🌐 {t('profile.sections.languageSettings', { defaultValue: 'Language Settings' })}</ModalTitle>
            <ModalSubtitle>
              {t('profile.languageDescription', { 
                defaultValue: 'Select your preferred language for the application interface. This will change all text, menus, and messages to your selected language.' 
              })}
            </ModalSubtitle>
          </ModalHeader>

          <LanguageGrid>
            {LANGUAGES.map((language) => {
              const isActive = language.code === currentLanguage.code;
              return (
                <LanguageOptionButton
                  key={language.code}
                  type="button"
                  $active={isActive}
                  onClick={() => handleLanguageChange(language.code)}
                >
                  <LanguageBadge>{language.shortLabel}</LanguageBadge>
                  <LanguageNativeName>{language.nativeName}</LanguageNativeName>
                  <LanguageLabel>{language.label}</LanguageLabel>
                </LanguageOptionButton>
              );
            })}
          </LanguageGrid>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default FloatingAboutButton;

